import {
  ExpandMoreIcon,
  Typography,
  Button,
  Grid,
  styledComponent,
  TextField,
  TuneIcon,
  Box,
  useTheme,
  ListItemText,
  Stack,
  ListItemIcon,
  Hidden
} from '@hermes/web-components'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AppDataContext } from '../../../../providers/AppData'
import { useTimeout } from '../../../../hooks/useTimeout'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import MobileSearchTypeModal from '../../../search/MobileSearchTypeModal'
import MobileFiltersModal from '../../../search/MobileFiltersModal'
import MobileSortByModal from '../../../search/MobileSortByModal'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import MobileSearchLocationModal from '../../../search/MobileSearchLocationModal'
import { capitalise } from '../../../../utils/strings'
import { MobileViewProps } from '../../common/types'
import { TEST_IDS } from '../../../../__tests__/testUtils/constants'
import useSearchBarConfig from '../../../search/hooks/useSearchBarConfig'
import { StyledMenuList, TypeMenuItem } from './styles'
import NextImage from 'next/image'
import { SearchTypes } from '../../../../providers/SearchProfilesPage/types'
import LoadingWheel from '../../../Icons/LoadingWheel'
import AutocompleteResultsModal from '../AutocompleteResultsModal'

type TypeMenuItemProps = {
  title: string
  value: SearchTypes
  isFirstItem: boolean
  isLastItem: boolean
  Icon: string
}

const MobileSearchTextField = styledComponent(TextField)(({ theme }) => ({
  height: '40px',
  borderRadius: '24px',
  marginTop: 8,
  '& .MuiInputBase-root': {
    borderRadius: '24px',
    'input::placeholder': {
      ...theme.typography.body1,
      color: 'black'
    }
  },
  '& .MuiInputBase-input': {
    textTransform: 'capitalize'
  }
}))

const MobileView = ({
  getYourLocation,
  onSearchFieldFocus,
  onChangeLocationSearch,
  renderSearchFilters,
  onChangeSearchType,
  sorting,
  ...autocompleteProps
}: MobileViewProps) => {
  const theme = useTheme()
  const { language } = useContext(AppDataContext)
  const {
    resetLocationsAutocomplete,
    state: { isKeywordChanged, isTypeChanged, isLocationChanged }
  } = useContext(SearchProfilesPageContext)
  const translate = useTranslateMessage()
  const timeout = useTimeout()
  const [isOpenSearchTypeModal, setIsOpenSearchTypeModal] = useState(false)
  const [isLocationFocused, setIsLocationFocused] = useState(false)
  const [searchBarExpand, setSearchBarExpand] = useState(false)
  const [isOpenSortByModal, setIsOpenSortByModal] = useState(false)
  const [isOpenFiltersModal, setIsOpenFiltersModal] = useState(false)
  const [openSearchTypeDropdown, setOpenSearchTypeDropdown] = useState(false)

  const menuContainer = useRef(null)

  const onToggleModal = () => setOpenSearchTypeDropdown((prev) => !prev)

  const onClose = () => setOpenSearchTypeDropdown(false)

  const {
    searchTypes,
    selectedSearchType,
    MobileProfileTypeLeftIcon,
    MobileProfileTypeRightIcon,
    MobileKeywordSearchLeftIconAlternative,
    MobileLocationSearchLeftIconAlternative
  } = useSearchBarConfig()

  const onLocationBlur = () =>
    timeout(() => {
      resetLocationsAutocomplete()
      setIsLocationFocused(false)
    })

  const expandSearchBar = () => setSearchBarExpand(true)
  const decreaseSearchBar = () => setSearchBarExpand(false)
  const onLocationFocus = () => timeout(() => setIsLocationFocused(true))

  const {
    changeFilter,
    state: { filters, specialty }
  } = useContext(SearchProfilesPageContext)

  useEffect(() => {
    const onScroll = () => {
      if (searchBarExpand) {
        decreaseSearchBar()
      }
    }
    document.addEventListener('scroll', onScroll)

    return () => {
      document.removeEventListener('scroll', onScroll)
    }
  }, [searchBarExpand])

  const handleMobileSearch = () => {
    decreaseSearchBar()
    // eslint-disable-next-line no-restricted-globals
    location.reload()
  }

  const handleSearchType = useCallback(
    (value: SearchTypes) => () => {
      onClose()
      onChangeSearchType(value)
    },
    [onChangeSearchType]
  )

  const renderTypeMenuItem = ({ title, value, isFirstItem, isLastItem, Icon }: TypeMenuItemProps) => (
    <TypeMenuItem value={value} isFirstItem={isFirstItem} isLastItem={isLastItem} onClick={handleSearchType(value)}>
      <Stack direction="row" justifyContent="space-between" p={0.5}>
        <ListItemIcon>
          <NextImage
            src={`/hermes-static/icons/${Icon}.svg`}
            alt=""
            width={22}
            height={22}
            objectFit="none"
            unoptimized
          />
        </ListItemIcon>
        <ListItemText>
          <Typography
            color="text.primary"
            variant="body1"
            textAlign="left"
            sx={filters?.searchType === value ? theme.custom.SearchBar.SelectedMenuItem : {}}
          >
            {title}
          </Typography>
        </ListItemText>
      </Stack>
    </TypeMenuItem>
  )

  return (
    <>
      <Box
        sx={{
          ...(theme?.custom?.SearchBar?.mobile?.section ?? {}),
          backgroundColor: theme?.custom?.SearchBar.backgroundColor ?? 'background.default'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: searchBarExpand ? '48px' : 0,
            opacity: searchBarExpand ? 1 : 0,
            transition: 'height .3s ease-out'
          }}
        >
          <MobileSearchTextField
            fullWidth
            inputProps={{
              'aria-label': 'Mobile Search Text Field'
            }}
            data-testid={TEST_IDS.SEARCH_BAR_MOBILE_VIEW_TEXT_FIELD_SPECIALIST}
            onClick={() => {
              if (!isTypeChanged) {
                onToggleModal()
              }
            }}
            placeholder={capitalise(selectedSearchType?.title)}
            value={capitalise(selectedSearchType?.title)}
            InputProps={{
              startAdornment: MobileProfileTypeLeftIcon ? MobileProfileTypeLeftIcon : null,
              endAdornment: !isTypeChanged ? (
                <MobileProfileTypeRightIcon
                  sx={{
                    color: 'inherit',
                    width: '16px',
                    height: '16px',
                    marginLeft: '10px',
                    marginRight: '17px'
                  }}
                />
              ) : (
                <LoadingWheel
                  style={{
                    marginRight: '10px'
                  }}
                />
              ),
              readOnly: true,
              sx: (themeIn) => ({
                ...themeIn.typography.body1,
                height: 40,
                padding: 0
              })
            }}
          />
          <StyledMenuList
            style={{
              zIndex: 99
            }}
            ref={menuContainer}
            isOpen={openSearchTypeDropdown}
          >
            {searchTypes.map(({ value, title, Icon }, index) => {
              const isFirstItem = index === 0
              const isLastItem = searchTypes.length - 1 === index
              return renderTypeMenuItem({ value, title, isFirstItem, isLastItem, Icon })
            })}
          </StyledMenuList>
        </Box>
        <MobileSearchTextField
          fullWidth
          placeholder={searchBarExpand ? specialty?.name[language] : translate('search.placeholder.find')}
          data-testid={TEST_IDS.SEARCH_BAR_MOBILE_VIEW_TEXT_FIELD}
          inputProps={{
            'aria-label': 'Mobile Search Text Field'
          }}
          sx={{
            width: '100%',
            position: 'relative'
          }}
          onClick={() => {
            if (!isKeywordChanged) {
              if (!searchBarExpand) {
                expandSearchBar()
              } else {
                onSearchFieldFocus()
              }
            }
          }}
          InputProps={{
            startAdornment: MobileKeywordSearchLeftIconAlternative ? (
              <MobileKeywordSearchLeftIconAlternative
                sx={{
                  color: theme.custom?.SearchFilters?.mobile?.textColor,
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  marginLeft: '17px',
                  marginRight: '10px'
                }}
              />
            ) : null,
            endAdornment: isKeywordChanged ? (
              <LoadingWheel
                style={{
                  marginLeft: '10px'
                }}
              />
            ) : null,
            readOnly: true,
            sx: (themeIn) => ({
              ...themeIn.typography.body1,
              height: 40,
              padding: '0 20px 0 0'
            })
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: searchBarExpand ? '96px' : 0,
            opacity: searchBarExpand ? 1 : 0,
            transition: 'height .3s ease-out'
          }}
        >
          <MobileSearchTextField
            fullWidth
            value={decodeURIComponent(filters.location)}
            onClick={() => {
              if (!isLocationChanged) {
                onLocationFocus()
              }
            }}
            data-testid={TEST_IDS.SEARCH_BAR_MOBILE_VIEW_TEXT_FIELD_LOCATION}
            placeholder={translate('search.placeholder.location')}
            InputProps={{
              startAdornment: MobileLocationSearchLeftIconAlternative ? (
                <MobileLocationSearchLeftIconAlternative
                  sx={{
                    color: theme.custom?.SearchFilters?.mobile?.textColor,
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    marginLeft: '17px',
                    marginRight: '10px'
                  }}
                />
              ) : null,
              endAdornment: isLocationChanged ? (
                <LoadingWheel
                  style={{
                    marginRight: '10px'
                  }}
                />
              ) : null,

              readOnly: true,
              sx: (themeIn) => ({
                ...themeIn.typography.body1,
                height: 40,
                padding: 0
              })
            }}
          />
          <Button
            data-testid={TEST_IDS.SEARCH_BAR_MOBILE_SEARCH_BUTTON}
            sx={{ ...(theme?.custom?.SearchBar?.mobile?.searchButton ?? {}) }}
            fullWidth
            onClick={handleMobileSearch}
          >
            <Typography variant="h4" color={theme?.custom?.SearchBar?.mobile?.searchButton?.color ?? 'white'}>
              {translate('search')}
            </Typography>
          </Button>
        </div>
      </Box>
      <Grid
        sx={{
          ...(theme?.custom?.SearchBar?.mobile?.section ?? {}),
          ...(theme?.custom?.SearchBar?.mobile?.controlsContainer ?? {})
        }}
        container
        justifyContent="space-between"
        mt={2}
        wrap="nowrap"
      >
        <Button
          sx={theme.custom?.SearchBar?.mobile?.buttonHover}
          size="small"
          data-testid={TEST_IDS.SEARCH_BAR_MOBILE_VIEW_SORT_BUTTON}
          fullWidth
          variant="outlined"
          onClick={() => setIsOpenSortByModal(true)}
        >
          <Typography variant="body2" color={theme.custom?.SearchFilters?.mobile?.mainColor}>
            {translate('sort_by')}
          </Typography>
          <Grid ml={0.5}>
            <ExpandMoreIcon
              sx={{
                color: theme.custom?.SearchFilters?.mobile?.mainColor,
                width: '12px',
                height: '12px',
                position: 'absolute',
                bottom: '4px'
              }}
            />
            <ExpandMoreIcon
              sx={{
                color: theme.custom?.SearchFilters?.mobile?.mainColor,
                width: '12px',
                height: '12px',
                position: 'absolute',
                transform: 'rotate(180deg)',
                top: '4px'
              }}
            />
          </Grid>
        </Button>
        <Button
          sx={theme.custom?.SearchBar?.mobile?.buttonHover}
          data-testid={TEST_IDS.SEARCH_BAR_MOBILE_VIEW_FILTER_BUTTON}
          fullWidth
          variant="outlined"
          onClick={() => setIsOpenFiltersModal(true)}
        >
          <Typography variant="body2" color={theme.custom?.SearchFilters?.mobile?.mainColor}>
            {'Filter'}
          </Typography>
          <TuneIcon
            sx={{
              marginLeft: 0.5,
              color: theme.custom?.SearchFilters?.mobile?.mainColor,
              width: '15px',
              height: '15px'
            }}
          />
        </Button>
      </Grid>
      <MobileSearchTypeModal
        isOpen={isOpenSearchTypeModal}
        onClose={() => {
          setIsOpenSearchTypeModal(false)
        }}
        selectedSearchType={selectedSearchType}
        onChangeSearchType={onChangeSearchType}
        searchTypes={searchTypes}
      />
      <MobileSearchLocationModal
        isLocationFocused={isLocationFocused}
        onLocationBlur={onLocationBlur}
        getYourLocation={getYourLocation}
        onChangeLocationSearch={onChangeLocationSearch}
      />
      <MobileFiltersModal
        isOpen={isOpenFiltersModal}
        onClose={() => setIsOpenFiltersModal(false)}
        renderSearchFilters={renderSearchFilters}
      />
      <MobileSortByModal
        currentSort={filters.sortType}
        isOpen={isOpenSortByModal}
        onClose={() => setIsOpenSortByModal(false)}
        sorting={sorting}
        changeFilter={changeFilter}
      />
      <Hidden lgUp>
        <AutocompleteResultsModal
          mobileView
          customStyle={autocompleteProps.customStyle}
          onClose={autocompleteProps.onSearchFieldBlur}
          onChangeSearch={autocompleteProps.onChangeSearch}
          isOpen={autocompleteProps.isSearchFieldFocused}
          setIsSearchFieldFocused={autocompleteProps.setIsSearchFieldFocused}
        />
      </Hidden>
    </>
  )
}

export default MobileView
